import { PRODUCTS_SLUG } from '../../shared/constants/SlugsProducts';
import { PLAN_SLUG, PLAN_SLUGS } from '../../shared/plans/catalog';
import {
	getItemProduct,
	getItemSlug,
	getOrderItems,
	groupOrderItems,
	PACKAGE_GROUP_SLUG,
} from './orderItem.util';
import { ANNUAL_DECLARATION_YEAR } from '../../features/onboarding/constants/annualDeclaration';
import { isPackageLine } from '../../features/regularization/offer/packageCart';
import { invoicingPlanCodeOf, isInvoicingPlanSlug } from '../../features/invoicing/v2/lib/quota';
import { sellingPlanNameOf } from '../../features/invoicing/v2/constants/planFeatures';
import type { CartSource } from '../../shared/checkout/cartSource';

export type PurchasedProductDisplay = {
	name: string;
	badge: string;
};

type DisplayOverride = Partial<PurchasedProductDisplay>;

const DEFAULT_DISPLAY: PurchasedProductDisplay = {
	name: 'servicio',
	badge: 'Pago único',
};

const PRODUCT_OVERRIDES: Record<string, DisplayOverride> = {
	// El paquete «Ponte al día». Nombre propio y no la suma de sus líneas: el
	// usuario compró "ponerse al día", no quince trámites. El desglose de qué
	// incluye va en la nota y en los chips de periodo, en chiquito.
	[PACKAGE_GROUP_SLUG]: {
		name: 'Regularización total',
		badge: 'Pago único',
	},
	[PRODUCTS_SLUG.ANNUAL_DECLARATION]: {
		name: `Declaración Anual ${ANNUAL_DECLARATION_YEAR}`,
		badge: 'Se renueva cada año',
	},
	[PRODUCTS_SLUG.NSS_INFO]: {
		name: 'Información NSS',
		badge: 'Pago único',
	},
	// Subscription plans (plan card / ally flow purchases). Recurring, so the
	// default "Pago único" badge would be wrong.
	[PLAN_SLUG.PLATAFORMAS]: { badge: 'Suscripción' },
	[PLAN_SLUG.PROFESIONISTA]: { badge: 'Suscripción' },
	[PLAN_SLUG.EMPRESARIAL]: { badge: 'Suscripción' },
};

const ACCOUNTANT_ELIGIBLE_SLUGS = new Set<string>([
	PRODUCTS_SLUG.ANNUAL_DECLARATION,
	PRODUCTS_SLUG.ANNUAL_DECLARATION_BASIC,
	PRODUCTS_SLUG.ANNUAL_DECLARATION_HARD,
	PRODUCTS_SLUG.ANNUAL_DECLARATION_BASIC_WITH_REGULARIZATION,
	PRODUCTS_SLUG.ANNUAL_DECLARATION_HARD_WITH_REGULARIZATION,
	PRODUCTS_SLUG.ANNUAL_DECLARATION_HARD_WITH_REGULARIZATION_COMPLEJA,
	PRODUCTS_SLUG.ANNUAL_DECLARATION_WITH_REGULARIZATION,
	PRODUCTS_SLUG.ANNUAL_DECLARATION_WITHOUT_REGULARIZATION,
	PRODUCTS_SLUG.MONTH_DECLARATION,
]);

/** An invoicing plan says how often it renews: it is sold monthly and yearly. */
const INVOICING_PLAN_BADGE: Record<'monthly' | 'annual', string> = {
	monthly: 'Suscripción mensual',
	annual: 'Suscripción anual',
};

/**
 * Resolves the display for a purchased product. Static PRODUCT_OVERRIDES drive
 * name/badge for known slugs; `fallbackName` (the product name
 * carried by the order item) covers the rest. No product catalog needed.
 */
export const resolvePurchasedProductDisplay = (
	slug: string | undefined,
	fallbackName?: string,
	periodicity?: 'monthly' | 'annual' | null,
): PurchasedProductDisplay => {
	const safeSlug = slug ?? '';
	const override = PRODUCT_OVERRIDES[safeSlug] ?? {};
	const invoicingBadge = isInvoicingPlanSlug(safeSlug)
		? (periodicity && INVOICING_PLAN_BADGE[periodicity]) || 'Suscripción'
		: undefined;

	return {
		name: override.name ?? fallbackName ?? DEFAULT_DISPLAY.name,
		badge: override.badge ?? invoicingBadge ?? DEFAULT_DISPLAY.badge,
	};
};

export const productHasAssignedAccountant = (slug: string | undefined) =>
	slug ? ACCOUNTANT_ELIGIBLE_SLUGS.has(slug) : false;

const PLAN_SLUG_SET: ReadonlySet<string> = new Set<string>(PLAN_SLUGS);

/** ¿Este slug es uno de los planes mensuales? */
export const isMonthlyPlanSlug = (slug: string | undefined): boolean =>
	Boolean(slug) && PLAN_SLUG_SET.has(slug as string);

/** ¿Este slug se renueva? Los planes mensuales y los de facturación por cupo. */
export const isSubscriptionSlug = (slug: string | undefined): boolean =>
	isMonthlyPlanSlug(slug) || isInvoicingPlanSlug(slug);

/**
 * ¿El carrito trae un plan mensual? Única fuente de esa pregunta: la usan el
 * subtítulo de la pantalla de éxito y la puerta del tour post-compra. Un
 * carrito mixto (plan + otra cosa) cuenta como plan — es lo que manda.
 */
export const orderHasMonthlyPlan = (order: unknown): boolean =>
	getOrderItems(order).some((item: unknown) =>
		isMonthlyPlanSlug(getItemSlug(item)),
	);

/**
 * ¿El carrito trae el paquete «Ponte al día»? Se pregunta por el sello que
 * el motor deja en TODAS sus líneas, no por slugs: el paquete no es
 * un producto, son las mismas líneas de regularización de siempre.
 */
export const orderHasPonteAlDiaPackage = (order: unknown): boolean =>
	getOrderItems(order).some((item: unknown) =>
		isPackageLine((item as Record<string, unknown>)?.additional_information),
	);

/**
 * ¿Alguna línea del carrito la vendió esa superficie? Se pregunta por el
 * `source` que deja su checkout y no por el slug: el mismo producto vendido en
 * otra superficie no abre el mismo recorrido.
 */
const orderHasSource = (order: unknown, source: CartSource): boolean =>
	getOrderItems(order).some(
		(item: unknown) =>
			(item as Record<string, { source?: unknown } | undefined>)
				?.additional_information?.source === source,
	);

/** ¿El carrito trae el trámite de sellos comprado desde el onboarding de facturación? */
export const orderHasOnboardingSealsAsat = (order: unknown): boolean =>
	orderHasSource(order, 'onboarding_seals_asat');

/** ¿El plan de facturación del carrito se compró en el onboarding «Solo me interesa facturar»? */
export const orderHasOnboardingInvoicingPlan = (order: unknown): boolean =>
	orderHasSource(order, 'onboarding_invoicing_plan');

export const orderHasAnnualDeclaration = (order: unknown): boolean =>
	getOrderItems(order).some((item: unknown) => getItemSlug(item) === PRODUCTS_SLUG.ANNUAL_DECLARATION);

export type OrderInvoicingPlan = {
	/** El código del plan en el cupo (`pro`, `plus`, `qr`): la llave de sus funciones. */
	code: string;
	/** Cómo lo nombra el lineup: «Pro», «Plus», «Facturación con QR». */
	name: string;
};

/**
 * El plan de facturación de una compra, o `undefined` si no trae. El nombre es
 * el del lineup y no el del producto, el mismo con el que lo nombran la hoja
 * del plan y el upsell; un código que el lineup no conoce cae al del producto.
 */
export const resolveOrderInvoicingPlan = (
	order: unknown,
): OrderInvoicingPlan | undefined => {
	const item = getOrderItems(order).find((candidate: unknown) =>
		isInvoicingPlanSlug(getItemSlug(candidate)),
	);
	if (!item) return undefined;
	const code = invoicingPlanCodeOf(getItemSlug(item));
	return {
		code,
		name: sellingPlanNameOf(code) ?? getItemProduct(item)?.name ?? code,
	};
};

/**
 * El nombre del plan mensual de una compra, para etiquetarla fuera del recibo.
 * Mismo resolvedor que la tarjeta del comprobante, así que la etiqueta y el
 * recibo no pueden discrepar. `undefined` cuando la compra no trae plan.
 */
export const resolveOrderPlanName = (order: unknown): string | undefined => {
	const plan = groupOrderItems(order).find((group) =>
		isMonthlyPlanSlug(group.slug),
	);
	if (!plan) return undefined;
	return resolvePurchasedProductDisplay(
		plan.slug,
		getItemProduct(plan.items[0])?.name,
	).name;
};
